import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, Monitor, Briefcase, ArrowRight, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const typeIcons = {
  meeting_room: Monitor,
  desk: Briefcase,
  equipment: Briefcase,
};

const typeLabels = {
  meeting_room: 'Meeting Room',
  desk: 'Desk',
  equipment: 'Equipment',
};

export default function DashboardPage() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/api/resources`, { withCredentials: true })
      .then(res => setResources(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const types = ['all', ...new Set(resources.map(r => r.resource_type))];
  const filtered = filter === 'all' ? resources : resources.filter(r => r.resource_type === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={24} className="animate-spin text-text-secondary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tighter text-white" data-testid="dashboard-heading">
          Resources
        </h1>
        <p className="text-text-secondary text-sm mt-2 font-body">
          Select a resource to book your time slot.
        </p>
      </div>

      {/* Filter Pills */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="resource-filter">
        {types.map(type => (
          <button
            key={type}
            onClick={() => setFilter(type)}
            data-testid={`filter-${type}`}
            className={`px-4 py-1.5 text-xs tracking-[0.15em] uppercase font-mono whitespace-nowrap transition-all duration-200 border ${
              filter === type
                ? 'bg-white text-black border-white'
                : 'bg-transparent text-text-secondary border-border hover:text-white hover:border-white/30'
            }`}
          >
            {type === 'all' ? 'All' : typeLabels[type] || type}
          </button>
        ))}
      </div>

      {/* Resource Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-20 text-text-secondary font-body" data-testid="no-resources">
          No resources available.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resource-grid">
          {filtered.map(resource => {
            const Icon = typeIcons[resource.resource_type] || Monitor;
            return (
              <Link
                key={resource.id}
                to={`/book/${resource.id}`}
                data-testid={`resource-card-${resource.id}`}
                className="group border border-border/40 bg-surface hover:bg-surface-hover p-6 transition-all duration-200 flex flex-col"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10">
                    <Icon size={18} strokeWidth={1.5} className="text-text-secondary group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-xs tracking-[0.15em] uppercase font-mono text-text-secondary">
                    {typeLabels[resource.resource_type] || resource.resource_type}
                  </span>
                </div>

                <h3 className="font-heading text-lg font-medium text-white mb-2 tracking-tight" data-testid={`resource-name-${resource.id}`}>
                  {resource.name}
                </h3>
                <p className="text-text-secondary text-sm font-body mb-4 flex-1 line-clamp-2">
                  {resource.description}
                </p>

                <div className="flex items-center justify-between mt-auto pt-4 border-t border-border/40">
                  <div className="flex items-center gap-1.5 text-text-secondary">
                    <Users size={14} strokeWidth={1.5} />
                    <span className="text-xs font-mono">{resource.capacity} {resource.capacity === 1 ? 'seat' : 'seats'}</span>
                  </div>
                  <div className="flex items-center gap-1 text-text-secondary group-hover:text-white transition-colors">
                    <span className="text-xs font-mono">Book</span>
                    <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
